import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MessageCircle, CheckSquare, Trash2, ClipboardList, X, Search } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { fmtDate, FY_OPTIONS, CURRENT_FY } from '../utils/helpers';
import { buildWaUrl } from '../utils/whatsapp';
import ConfirmModal from '../components/ConfirmModal';
import ClientSearchDropdown from '../components/ClientSearchDropdown';
import BulkActionBar from '../components/BulkActionBar';
import toast from 'react-hot-toast';

const WORK_TYPES = ['ITR', 'GST', 'TDS', 'Audit', 'ROC', 'ESI-PF', 'Accounts'];
const STATUSES = ['Pending', 'In Progress', 'Completed'];

const WORK_TYPE_COLOR = {
  ITR:     'bg-blue-100 text-blue-700',
  GST:     'bg-orange-100 text-orange-700',
  TDS:     'bg-purple-100 text-purple-700',
  Audit:   'bg-red-100 text-red-700',
  ROC:     'bg-indigo-100 text-indigo-700',
  'ESI-PF':'bg-teal-100 text-teal-700',
  Accounts:'bg-green-100 text-green-700',
};

const STATUS_COLOR = {
  Pending:     'bg-gray-100 text-gray-600',
  'In Progress':'bg-yellow-100 text-yellow-700',
  Completed:   'bg-green-100 text-green-700',
};

// Module-scope: prevents remount on parent re-render
function CreateModal({ staff, onCreated, onClose }) {
  const [form, setForm] = useState({ client_id: null, work_type: 'ITR', fy: CURRENT_FY, period: '', assigned_to: '' });
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [customItems, setCustomItems] = useState([]);
  const [loadingTmpl, setLoadingTmpl] = useState(false);

  const loadTemplates = useCallback(async (wt) => {
    setLoadingTmpl(true);
    try {
      const r = await api.get('/checklists/templates', { params: { work_type: wt } });
      setTemplates(r.data);
      setCustomItems(r.data.map(t => ({ ...t, include: true })));
    } catch { /* silent */ }
    finally { setLoadingTmpl(false); }
  }, []);

  useEffect(() => { loadTemplates(form.work_type); }, [form.work_type, loadTemplates]);

  const save = async () => {
    if (!form.client_id) { toast.error('Select a client'); return; }
    setSaving(true);
    try {
      const items = customItems.filter(i => i.include).map(i => ({ id: i.id, item_name: i.item_name, is_mandatory: i.is_mandatory }));
      const r = await api.post('/checklists', { ...form, custom_items: items.length ? items : undefined });
      toast.success('Checklist created');
      onCreated(r.data.id);
    } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><ClipboardList size={18} /> New Checklist</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
            <ClientSearchDropdown value={form.client_id || ''} initialLabel="" onChange={id => setForm(f => ({ ...f, client_id: id }))} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Work Type *</label>
              <select value={form.work_type} onChange={e => setForm(f => ({ ...f, work_type: e.target.value }))} className="input-field">
                {WORK_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">FY</label>
              <select value={form.fy} onChange={e => setForm(f => ({ ...f, fy: e.target.value }))} className="input-field">
                {FY_OPTIONS.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Period (optional)</label>
              <input value={form.period} onChange={e => setForm(f => ({ ...f, period: e.target.value }))} className="input-field" placeholder="e.g. Apr 2025, Q1" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
              <select value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))} className="input-field">
                <option value="">Not assigned</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          {/* Template items preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Documents checklist ({customItems.filter(i => i.include).length} items selected)
              {loadingTmpl && <span className="text-gray-400 ml-2">Loading…</span>}
            </label>
            <div className="border border-gray-100 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
              {customItems.map((item, i) => (
                <div key={i} className={`flex items-center gap-3 px-3 py-2 text-sm border-b border-gray-50 last:border-0 ${item.include ? '' : 'opacity-50'}`}>
                  <input type="checkbox" checked={item.include} onChange={e => {
                    const copy = [...customItems]; copy[i] = { ...copy[i], include: e.target.checked }; setCustomItems(copy);
                  }} className="rounded" />
                  <span className="flex-1 text-gray-700">{item.item_name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${item.is_mandatory ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-gray-400'}`}>
                    {item.is_mandatory ? 'Required' : 'Optional'}
                  </span>
                </div>
              ))}
              {!loadingTmpl && !customItems.length && <p className="text-sm text-gray-400 p-3 text-center">No template items found</p>}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button onClick={save} disabled={saving} className="flex-1 btn-primary py-2.5">{saving ? 'Creating…' : 'Create Checklist'}</button>
          <button onClick={onClose} className="flex-1 btn-secondary py-2.5">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function ChecklistsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checklists, setChecklists] = useState([]);
  const [stats, setStats] = useState(null);
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState('');
  const [workTypeFilter, setWorkTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fyFilter, setFyFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reminderLoading, setReminderLoading] = useState(null);

  // Alt+I — open the Create Checklist form
  useEffect(() => {
    const h = () => setShowCreate(true);
    window.addEventListener('pms:new-record', h);
    return () => window.removeEventListener('pms:new-record', h);
  }, []);

  // Bulk select + archive
  const [selected, setSelected] = useState(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const toggleSel = id => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const load = useCallback(async () => {
    const params = {};
    if (workTypeFilter) params.work_type = workTypeFilter;
    if (statusFilter) params.status = statusFilter;
    if (fyFilter) params.fy = fyFilter;
    if (showArchived) params.show_archived = '1';
    try {
      const [cl, st] = await Promise.all([
        api.get('/checklists', { params }),
        api.get('/checklists/stats'),
      ]);
      setChecklists(cl.data);
      setStats(st.data);
      setSelected(new Set());
    } catch { /* silent */ }
  }, [workTypeFilter, statusFilter, fyFilter, showArchived]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/auth/users').then(r => setStaff(r.data)).catch(() => {}); }, []);

  const handleDelete = async () => {
    try {
      await api.delete(`/checklists/${deleteTarget.id}`);
      toast.success('Checklist deleted');
      setDeleteTarget(null);
      load();
    } catch { toast.error('Failed to delete'); }
  };

  // ── Bulk actions ────────────────────────────────────────────────────────────
  const bulkArchive = async archive => {
    try { await api.post('/checklists/bulk-archive', { ids: [...selected], archive }); toast.success(archive ? 'Archived' : 'Unarchived'); load(); }
    catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };
  const bulkDelete = async () => {
    try { await api.post('/checklists/bulk-delete', { ids: [...selected] }); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };

  const sendReminder = async (cl) => {
    setReminderLoading(cl.id);
    try {
      const r = await api.post(`/checklists/${cl.id}/send-reminder`);
      if (!r.data.whatsapp) {
        toast.error('No WhatsApp number on file for this client');
        return;
      }
      const url = buildWaUrl(r.data.whatsapp, r.data.message);
      if (url) window.open(url, '_blank');
      toast.success(`WhatsApp reminder ready — ${r.data.pending_count} items`);
      load();
    } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
    finally { setReminderLoading(null); }
  };

  const filtered = checklists.filter(cl => {
    if (!search) return true;
    const s = search.toLowerCase();
    return cl.client_name?.toLowerCase().includes(s) || cl.client_code?.toLowerCase().includes(s);
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-[#1e3a5f]">Document Checklists</h1>
          <p className="text-sm text-gray-500 mt-0.5">Track documents received from clients for each compliance</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2">
          <Plus size={16} /> New Checklist
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <div className="card text-center">
            <div className="text-2xl font-bold text-[#1e3a5f]">{stats.active}</div>
            <div className="text-xs text-gray-500 mt-1">Active Checklists</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-orange-600">{stats.pending_docs}</div>
            <div className="text-xs text-gray-500 mt-1">Documents Pending</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-green-600">{stats.completed_month}</div>
            <div className="text-xs text-gray-500 mt-1">Completed This Month</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-44">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search client…"
            className="input-field pl-9 text-sm" />
        </div>
        <select value={workTypeFilter} onChange={e => setWorkTypeFilter(e.target.value)} className="input-field w-36">
          <option value="">All Types</option>
          {WORK_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="input-field w-36">
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={fyFilter} onChange={e => setFyFilter(e.target.value)} className="input-field w-32">
          <option value="">All FY</option>
          {FY_OPTIONS.map(f => <option key={f}>{f}</option>)}
        </select>
        <button onClick={() => setShowArchived(a => !a)}
          className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${showArchived ? 'bg-amber-50 border-amber-300 text-amber-700' : 'border-gray-300 text-gray-500 hover:border-gray-400'}`}>
          {showArchived ? '📂 Archived' : '📁 Show Archived'}
        </button>
      </div>

      {/* Checklist cards */}
      {filtered.length === 0 ? (
        <div className="card text-center py-12 text-gray-400">
          <ClipboardList size={40} className="mx-auto mb-3 text-gray-200" />
          <p className="font-medium">No checklists found</p>
          <p className="text-sm mt-1">Create a checklist to start tracking documents from clients.</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary mt-4 mx-auto flex items-center gap-2 text-sm">
            <Plus size={14} /> Create First Checklist
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer pl-1">
              <input type="checkbox" checked={filtered.length > 0 && selected.size === filtered.length}
                onChange={e => setSelected(e.target.checked ? new Set(filtered.map(c => c.id)) : new Set())}
                className="rounded" />
              Select all ({filtered.length})
            </label>
          </div>
          <BulkActionBar module="checklists" count={selected.size}
            onArchive={archive => bulkArchive(archive)}
            archivedView={showArchived}
            onDelete={bulkDelete}
            onClear={() => setSelected(new Set())} />
          {filtered.map(cl => {
            const pct = cl.total_items ? Math.round((cl.received_items / cl.total_items) * 100) : 0;
            return (
              <div key={cl.id} className={`card hover:shadow-md hover:border-[#f0a500] transition-all cursor-pointer ${selected.has(cl.id) ? 'ring-2 ring-blue-300 bg-blue-50/30' : ''}`} onClick={() => navigate(`/checklists/${cl.id}`)}>
                <div className="flex items-start gap-3">
                  <input type="checkbox" checked={selected.has(cl.id)} onClick={e => e.stopPropagation()}
                    onChange={() => toggleSel(cl.id)} className="rounded mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-semibold text-[#1e3a5f] text-sm">{cl.client_name}</span>
                      {cl.client_code && <span className="text-xs text-gray-400">{cl.client_code}</span>}
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${WORK_TYPE_COLOR[cl.work_type] || 'bg-gray-100 text-gray-600'}`}>{cl.work_type}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[cl.status] || 'bg-gray-100 text-gray-600'}`}>{cl.status}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-2">
                      {cl.fy && <span>FY {cl.fy}</span>}
                      {cl.period && <span>· {cl.period}</span>}
                      {cl.assigned_to_name && <span>· {cl.assigned_to_name}</span>}
                      <span>· Created {fmtDate(cl.created_at?.split('T')[0] || cl.created_at?.split(' ')[0])}</span>
                    </div>
                    {/* Progress bar */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-100 rounded-full h-1.5 max-w-xs">
                        <div className={`h-1.5 rounded-full transition-all ${pct === 100 ? 'bg-green-500' : pct > 0 ? 'bg-blue-500' : 'bg-gray-300'}`}
                          style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-medium text-gray-600">{cl.received_items}/{cl.total_items} received</span>
                      {cl.pending_items > 0 && (
                        <span className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded-full font-medium">{cl.pending_items} pending</span>
                      )}
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    {cl.pending_items > 0 && (
                      <button onClick={() => sendReminder(cl)} disabled={reminderLoading === cl.id}
                        className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 px-2 py-1.5 rounded-lg hover:bg-green-50 transition-colors">
                        <MessageCircle size={13} /> {reminderLoading === cl.id ? '…' : 'Remind'}
                      </button>
                    )}
                    {cl.status !== 'Completed' && cl.pending_items === 0 && cl.total_items > 0 && (
                      <button onClick={async () => {
                        await api.put(`/checklists/${cl.id}`, { ...cl, status: 'Completed' });
                        load();
                      }} className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 px-2 py-1.5 rounded-lg hover:bg-green-50 transition-colors">
                        <CheckSquare size={13} /> Complete
                      </button>
                    )}
                    <button onClick={() => setDeleteTarget(cl)}
                      className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && <CreateModal staff={staff} onCreated={id => { setShowCreate(false); load(); }} onClose={() => setShowCreate(false)} />}
      {deleteTarget && (
        <ConfirmModal variant="delete" message={`Delete checklist for ${deleteTarget.client_name} (${deleteTarget.work_type})?`}
          onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
      )}
    </div>
  );
}
